import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { scoringApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import type { ScoringRunInput } from '../types/api.js';

/**
 * @graph-stack mall
 * @graph-domain Operations
 * @graph-business スコアリングフック。スコアリング実行ミューテーション・履歴・詳細のクエリを統合提供
 * @graph-connects scoringApi [calls] スコアリングAPIクライアント経由でスコアリング実行・履歴・詳細取得
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useRunScoring() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ScoringRunInput) => scoringApi.run(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.scoring.all });
    },
  });
}

/**
 * @graph-stack mall
 * @graph-domain Operations
 * @graph-business スコアリング履歴フック。履歴一覧をTanStack Queryで取得
 * @graph-connects scoringApi [calls] スコアリングAPIクライアント経由で履歴取得
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useScoringHistory(limit?: number) {
  return useQuery({
    queryKey: queryKeys.scoring.history(limit),
    queryFn: () => scoringApi.getHistory(limit),
  });
}

/**
 * @graph-stack mall
 * @graph-domain Operations
 * @graph-business スコアリング詳細フック。IDを指定してスコアリング結果をTanStack Queryで取得
 * @graph-connects scoringApi [calls] スコアリングAPIクライアント経由で詳細取得
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useScoringDetail(id: string) {
  return useQuery({
    queryKey: queryKeys.scoring.detail(id),
    queryFn: () => scoringApi.getById(id),
    enabled: !!id,
  });
}
